/**
 * Mining Calculations Engine & Production Verification Helpers
 * Authoritative Server Accrual Model
 */
import { MAIN_MINER_DAILY_RATE, MAIN_MINER_RATE_PER_SECOND, AUTO_MINER_DAILY_RATE, AUTO_MINER_RATE_PER_SECOND, AUTO_MINER_REQUIRED_TASKS, STARS_MINER_DAILY_RATE_PER_UNIT, STARS_MINER_RATE_PER_SECOND_PER_UNIT, SECONDS_PER_DAY, MAX_ACCUMULATION_SECONDS, } from '../constants/mining.js';
/**
 * Production Accrual Model:
 * cumulativeEarned = floor(elapsedActiveSeconds * dailyRate / 86400)
 * claimable = cumulativeEarned - totalPreviouslyClaimed
 *
 * Accounts for pauses and stop times.
 * Preserves fractional progress across partial claims without losing earned progress.
 *
 * @param activatedAtMs Server timestamp (ms) when miner became active
 * @param nowMs Authoritative server timestamp (ms)
 * @param dailyRate Coins produced per 86,400 seconds (2,000 for main/auto, 100,000 * units for stars)
 * @param totalPreviouslyClaimed Total integer coins previously claimed from this miner
 * @param pausedAtMs Optional server timestamp (ms) when miner was paused or stopped
 */
export function calculateMinerAccrual(activatedAtMs, nowMs, dailyRate, totalPreviouslyClaimed = 0, pausedAtMs) {
    if (activatedAtMs <= 0 || nowMs <= activatedAtMs || dailyRate <= 0) {
        return {
            cumulativeEarned: 0,
            claimable: 0,
            elapsedActiveSeconds: 0,
            ratePerSecond: dailyRate > 0 ? dailyRate / SECONDS_PER_DAY : 0,
        };
    }
    const effectiveEndMs = pausedAtMs && pausedAtMs > activatedAtMs
        ? Math.min(nowMs, pausedAtMs)
        : nowMs;
    const elapsedActiveSeconds = Math.max(0, Math.floor((effectiveEndMs - activatedAtMs) / 1000));
    const cumulativeEarned = Math.floor((elapsedActiveSeconds * dailyRate) / SECONDS_PER_DAY);
    const safePreviousClaims = Math.max(0, Math.floor(totalPreviouslyClaimed));
    const claimable = Math.max(0, cumulativeEarned - safePreviousClaims);
    return {
        cumulativeEarned,
        claimable,
        elapsedActiveSeconds,
        ratePerSecond: dailyRate / SECONDS_PER_DAY,
    };
}
/**
 * Legacy/continuous accrual helper for smooth UI interpolation
 */
export function calculateAccruedCoins(lastClaimedAtMs, currentTimeMs = Date.now(), dailyRate, maxBufferSeconds = MAX_ACCUMULATION_SECONDS) {
    if (lastClaimedAtMs <= 0 || currentTimeMs <= lastClaimedAtMs || dailyRate <= 0) {
        return 0;
    }
    const elapsedSeconds = Math.max(0, (currentTimeMs - lastClaimedAtMs) / 1000);
    const effectiveSeconds = maxBufferSeconds > 0
        ? Math.min(elapsedSeconds, maxBufferSeconds)
        : elapsedSeconds;
    const ratePerSecond = dailyRate / SECONDS_PER_DAY;
    return effectiveSeconds * ratePerSecond;
}
/**
 * Checks whether Auto Miner is unlocked based on eligible completed website tasks
 */
export function isAutoMinerUnlocked(completedTasksCount) {
    return completedTasksCount >= AUTO_MINER_REQUIRED_TASKS;
}
/**
 * Calculates aggregate mining production rates across all active miner sources
 */
export function calculateAggregateMiningRates(isMainActive, isAutoActiveAndUnlocked, starsUnitsCount) {
    let dailyRate = 0;
    let ratePerSecond = 0;
    if (isMainActive) {
        dailyRate += MAIN_MINER_DAILY_RATE;
        ratePerSecond += MAIN_MINER_RATE_PER_SECOND;
    }
    if (isAutoActiveAndUnlocked) {
        dailyRate += AUTO_MINER_DAILY_RATE;
        ratePerSecond += AUTO_MINER_RATE_PER_SECOND;
    }
    if (starsUnitsCount > 0) {
        dailyRate += starsUnitsCount * STARS_MINER_DAILY_RATE_PER_UNIT;
        ratePerSecond += starsUnitsCount * STARS_MINER_RATE_PER_SECOND_PER_UNIT;
    }
    return {
        dailyRate,
        ratePerSecond,
    };
}
/**
 * Format coin numbers nicely with configurable precision
 */
export function formatCoinAmount(amount, decimals = 4) {
    if (isNaN(amount) || amount === null || amount === undefined) {
        return '0.0000';
    }
    return amount.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}
//# sourceMappingURL=mining.js.map